import { useRef, useState } from "react";
import { Upload, X, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { resolveImg } from "@/lib/format";

export function ImageUpload({ value, onChange, label = "Image", rounded = false, className = "" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/institution/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-4">
        <div className={`flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden border border-slate-200 bg-slate-50 ${rounded ? "rounded-full" : "rounded-lg"}`}>
          {value ? <img src={resolveImg(value)} alt={label} className="h-full w-full object-cover" />
            : <ImageIcon className="h-7 w-7 text-slate-300" />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handle} data-testid={`upload-input-${label.toLowerCase().replace(/\s+/g, "-")}`} />
          <Button type="button" variant="outline" size="sm" onClick={pick} disabled={uploading} data-testid={`upload-btn-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {uploading ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Uploading…</> : <><Upload className="mr-1.5 h-4 w-4" />Upload {label}</>}
          </Button>
          {value && (
            <button type="button" onClick={() => onChange("")} className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500">
              <X className="h-3 w-3" /> Remove
            </button>
          )}
        </div>
      </div>
      <p className="mt-1.5 text-xs text-slate-400">PNG, JPG, WEBP up to 5MB.</p>
    </div>
  );
}
